import { defineStore } from "pinia";
import { ref } from "vue";
import * as API from "@/api/API";
import { showPrompt } from "@core/ui/alert/alert";

export const useAdmin = defineStore("admin", () => {
  const adminToken = ref<string | null>(null);
  const adminTokenValidUntil = ref<string | null>(null);
  const adminTokenRequest = ref<Promise<string> | null>(null);

  async function performAdminTokenRequest() {
    if (adminTokenRequest.value != null) {
      console.log("waiting for existing admin token request");
      return adminTokenRequest.value;
    }

    adminTokenRequest.value = new Promise(async (resolve, reject) => {
      console.log("requesting new admin token");
      try {
        await requestAdminTokenEmail();
      } catch (e) {
        reject(e);
        adminTokenRequest.value = null;
        return;
      }

      const result = await showPrompt<{
        verificationCode: string;
      }>({
        "title": "Bestätigungscode",
        "text": "Du hast einen Code per E-Mail erhalten. Bitte gib ihn hier ein.",
        "content": [
          {
            "type": "input",
            "value": "",
            "label": "Bestätigungscode",
            "name": "verificationCode",
            "required": true
          }
        ]
      });
      if (result == null) {
        reject(new Error("No verification code provided"));
        adminTokenRequest.value = null;
        return;
      }

      const { verificationCode } = result;

      try {
        const token = await requestAdminToken(verificationCode);

        resolve(token);
      } catch (e) {
        console.log(e);
        reject(e);
        adminTokenRequest.value = null;
        return;
      }

      adminTokenRequest.value = null;
    });

    return adminTokenRequest.value;
  }

  function requestAdminTokenEmail() {
    return API.post("/v1/admin/tokens/request_code", {});
  }

  async function requestAdminToken(verificationCode: string) {
    const { token, validUntil } = await API.post<{
      token: string;
      validUntil: string;
    }>("/v1/admin/tokens", {
      data: {
        verificationCode
      }
    });

    adminToken.value = token;
    adminTokenValidUntil.value = validUntil;

    return token;
  }

  type APIFunction = typeof API.get | typeof API.post | typeof API.destroy | typeof API.put | typeof API.patch;

  async function wrapMethod<T = unknown>(method: APIFunction, url: string, requestOptions: API.RequestOptions = {}) {
    const prefixedUrl = "/v1/admin" + url;
    try {
      const headers: Record<string, string> = {};
      if (adminToken.value != null) {
        headers["ADMIN-AUTHORIZATION"] = adminToken.value;
      }
      return await method<T>(prefixedUrl, {
        ...requestOptions,
        headers
      });
    } catch (e) {
      const error = e as Error;
      if ("code" in error && error.code == "no_valid_admin_access_token") {
        try {
          await performAdminTokenRequest();
        } catch (e) {
          console.log(e);
          throw error;
        }

        return wrapMethod(method, url, requestOptions);
      }
      throw error;
    }
  }

  const get = <T = unknown>(url: string, options: API.RequestOptions = {}) => wrapMethod<T>(API.get, url, options);
  const post = <T = unknown>(url: string, options: API.RequestOptions = {}) => wrapMethod<T>(API.post, url, options);
  const destroy = <T = unknown>(url: string, options: API.RequestOptions = {}) => wrapMethod<T>(API.destroy, url, options);
  const put = <T = unknown>(url: string, options: API.RequestOptions = {}) => wrapMethod<T>(API.put, url, options);
  const patch = <T = unknown>(url: string, options: API.RequestOptions = {}) => wrapMethod<T>(API.patch, url, options);

  return {
    adminToken,
    get,
    post,
    destroy,
    put,
    patch,
    requestAdminTokenEmail,
    requestAdminToken
  };
});